<script lang="ts">
  import { link } from 'svelte-routing';
  import { Row, Col, Button } from "sveltestrap"
  import TokenInput, { value } from "./TokenInput.svelte"
  import CurrencyBadge from "./CurrencyBadge.svelte"
  import WalletConnect from "./WalletConnect.svelte"
  import { getPairDetail } from "../stores/query"
  import {
    coreContract,
    viewCoreContract,
    pairDetail,
    tokenPrice,
    viewWeb3,
    multipliers,
    currentChainId,
  } from "../stores/store"
  import { chainId, connected, makeEvmStores, selectedAccount, web3 } from "svelte-web3"
  import { ERC20ABI } from "../abis/erc20"
  import PilTooltip from "./PilTooltip.svelte"
  import TxModal from "./TxModal.svelte"
  import { nFormatter, shortenAddress, txLink } from "../utils/utils"
  import { PIL } from "../stores/constants"
  import { COREABI } from "../abis/core"
  import { CONFIG } from "../config"

  const ROUND = 'ROUND';

  let payAmount = 0;
  let receiveAmount = 0;
  let roundBalance = 0;
  let baseBalance = 0;
  let roundPrice = 0;
  let baseToken = 'PIL';
  let phase = 'input' // input -> confirm -> submit
  let payRound = false;
  let isApproved = false;
  let txHash = '';
  let approveDisabled = false;
  let approveModalOpen = false;
  let confirmDisabled = false;
  let confirmModalOpen = false;

  const swapCurrencies = () => {
    payRound = !payRound;
    payAmount = 0;
    receiveAmount = 0;
  }

  const reset = () => {
    payAmount = 0;
    receiveAmount = 0;
    phase = 'input'
  }

  const getRoundBalance = async () => {
    if (!$connected || $getPairDetail.fetching) return 0;
    roundBalance = Number.parseFloat(await $coreContract.methods.balanceOf($getPairDetail.data.pair.id, $selectedAccount).call()) / 1e18;
  }

  const getBaseBalance = async () => {
    if (!$connected || $getPairDetail.fetching) return 0;
    const tokenContract = new $web3.eth.Contract(ERC20ABI, $getPairDetail.data.pair.baseToken.id);
    baseBalance = Number.parseFloat(await tokenContract.methods.balanceOf($selectedAccount).call()) / 10 ** Number.parseInt($getPairDetail.data.pair.baseToken.decimals);
    const allowance = $web3.utils.toBN(await tokenContract.methods.allowance($selectedAccount, CONFIG[$chainId].CORE_ADDRESS).call());
    isApproved = allowance.gt($web3.utils.toBN(0));
  }

  const approve = async () => {
    const tokenContract = new $web3.eth.Contract(ERC20ABI, $getPairDetail.data.pair.baseToken.id);
    tokenContract.methods.approve(
      CONFIG[$chainId].CORE_ADDRESS,
      '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
    ).send({ from: $selectedAccount })
      .on('transactionHash', hash => {
        console.log(hash);
        approveDisabled = true;
        approveModalOpen = true;
        txHash = hash;
      })
      .on('receipt', receipt => {
        console.log(receipt);
        isApproved = true;
        approveDisabled = false;
      })
  }

  const quoteReceive = async () => {
    if ($viewCoreContract === undefined) return 0;
    const toBN = $viewWeb3.utils.toBN;
    const baseDecimals = Number.parseInt($getPairDetail.data.pair.baseToken.decimals)
    const payDecimals = payRound ? 18 : baseDecimals;
    const receiveDecimals = payRound ? baseDecimals : 18;
    const input = toBN((payAmount * Math.pow(10, payDecimals)).toLocaleString('fullwide', { useGrouping: false }));
    const method = payRound ? $viewCoreContract.methods.quoteSellExactRounds : $viewCoreContract.methods.quoteBuyWithExactBases;
    receiveAmount = await method($getPairDetail.data.pair.id, input).call() / Math.pow(10, receiveDecimals);
  }

  const quotePay = async () => {
    if ($viewCoreContract === undefined) return 0;
    const toBN = $viewWeb3.utils.toBN;
    const baseDecimals = Number.parseInt($getPairDetail.data.pair.baseToken.decimals)
    const payDecimals = payRound ? 18 : baseDecimals;
    const receiveDecimals = payRound ? baseDecimals : 18;
    const input = toBN((receiveAmount * Math.pow(10, receiveDecimals)).toLocaleString('fullwide', { useGrouping: false }));
    const method = payRound ? $viewCoreContract.methods.quoteSellWithExactBases : $viewCoreContract.methods.quoteBuyExactRounds;
    payAmount = await method($getPairDetail.data.pair.id, input).call() / Math.pow(10, payDecimals);
  }

  const swap = async () => {
    const pilgrim = new $web3.eth.Contract(COREABI, CONFIG[$chainId].CORE_ADDRESS);
    const baseDecimals = Number.parseInt($getPairDetail.data.pair.baseToken.decimals)
    const payDecimals = payRound ? 18 : baseDecimals;
    const receiveDecimals = payRound ? baseDecimals : 18;
    let method;
    if (payRound) {
      const roundIn = $web3.utils.toBN(Math.floor(payAmount * Math.pow(10, payDecimals)).toLocaleString('fullwide', { useGrouping: false }));
      const minOut = $web3.utils.toBN(Math.floor(receiveAmount * 0.99 * Math.pow(10, receiveDecimals)).toLocaleString('fullwide', { useGrouping: false }));
      method = pilgrim.methods.sellExactRoundsWithBases($getPairDetail.data.pair.id, roundIn, minOut, Math.round(Date.now() / 1000 + 30000))
    } else {
      const roundOut = $web3.utils.toBN(Math.floor(receiveAmount * 0.99 * Math.pow(10, receiveDecimals)).toLocaleString('fullwide', { useGrouping: false }));
      const maxIn = $web3.utils.toBN(Math.floor(payAmount * Math.pow(10, payDecimals)).toLocaleString('fullwide', { useGrouping: false }));
      method = pilgrim.methods.buyExactRoundsWithBases($getPairDetail.data.pair.id, maxIn, roundOut, Math.round(Date.now() / 1000 + 30000))
    }
    method.send({ from: $selectedAccount })
      .on('transactionHash', hash => {
        console.log(hash);
        txHash = hash;
        confirmDisabled = true;
        confirmModalOpen = true;
      })
      .on('receipt', receipt => {
        console.log(receipt);
        phase = 'submit';
        confirmDisabled = false;
      })
  }

  const payAmountInput = (e: Event) => {
    if (e !== undefined) {
      const v = Number.parseFloat((e.target as HTMLInputElement).value);
      if (isNaN(v)) {
        payAmount = 0;
        receiveAmount = 0;
      } else {
        payAmount = v;
        quoteReceive();
      }
    }
  };

  const receiveAmountInput = (e: Event) => {
    if (e !== undefined) {
      const v = Number.parseFloat((e.target as HTMLInputElement).value);
      if (isNaN(v)) {
        payAmount = 0;
        receiveAmount = 0;
      } else {
        receiveAmount = v;
        quotePay();
      }
    }
  };

  $: try{
    if ($connected && $coreContract !== undefined && !$getPairDetail.fetching) getRoundBalance();
  }  catch (e) {
    console.error(e);
  }

  $: try {
    if (!$getPairDetail.fetching && $getPairDetail.data !== undefined && $getPairDetail.data.pair !== null) {
      baseToken = $getPairDetail.data.pair.baseToken.symbol;
      roundPrice = Number.parseFloat($getPairDetail.data.pair.roundTokenPrice);
    }
  } catch (e) {
    console.error(e);
  }

  $: try {
    if ($connected && !$getPairDetail.fetching) {
      getBaseBalance();
    }
  } catch (e) {
    console.error(e);
  }
</script>

{#if phase === 'input'}
  <Row class="text-center trade-section">
    <Col>
      <div class="trade-detail-label">
        PIL APR
        <PilTooltip tooltipId="trade-pil-apy">Annualized PIL returns from this pool.</PilTooltip>
      </div>
      <div class="trade-detail-value">
        {nFormatter($pairDetail.yearPairRewardPerRound * $tokenPrice.PIL / ($pairDetail.roundTokenPrice * $tokenPrice[$pairDetail.denominator.symbol]) * 100, 2)}%
      </div>
    </Col>
    <Col>
      <div class="trade-detail-label">
        Multiplier
        <PilTooltip tooltipId="trade-multiplier">Rewards multiplier over base PIL emissions for this pool.</PilTooltip>
      </div>
      <div class="trade-detail-value">{$multipliers[baseToken]}x</div>
    </Col>
  </Row>
  <Row class="trade-margin"/>
  <Row class="trade-input-label trade-section">
    You pay
    <TokenInput
      currency={payRound? ROUND : baseToken}
      balance={payRound ? roundBalance : baseBalance}
      valid={payAmount <= (payRound ? roundBalance : baseBalance)}
      value={payAmount}
      onInput={payAmountInput}
    />
  </Row>
  {#if payAmount > (payRound ? roundBalance : baseBalance)}
    <Row class="trade-section gx-3">
      <Col class="col-auto">
        <img class="icon-small" src="/icons/warning.svg" alt="placeholder"/>
      </Col>
      <Col class="warning-msg">
        You have insufficient fund in your wallet.<br/>
        Deposit more funds in order to make a successful transaction.
      </Col>
    </Row>
  {/if}
  <Row class="trade-section justify-content-center align-items-center">
    <div class="trade-divider"></div>
    <Col class="col-auto">
      <img class="icon-large trade-change-button" src="/icons/arrow-up-down.svg" alt="placeholder" on:click={swapCurrencies}/>
    </Col>
  </Row>
  <Row class="trade-section justify-content-end">
    <Col class="col-auto">
      <div class="token-price trade-round-price">1 Round = {roundPrice.toLocaleString('en')} {baseToken}</div>
    </Col>
  </Row>
  <Row class="trade-input-label trade-section" style="margin-top: 1rem">
    You receive
    <TokenInput
      currency={payRound? baseToken : ROUND}
      balance= {payRound? baseBalance : roundBalance}
      valid={true}
      value={receiveAmount}
      onInput={receiveAmountInput}
    />
  </Row>
  {#if payAmount > 0}
    <Row class="trade-section gx-0">
      <Col>
        <Row class="trade-announce gx-0">
          <Col class="col-auto trade-announce-icon">
            <img class="icon-small" src="/icons/announce.svg" alt="placeholder"/>
          </Col>
          <Col>
            Transaction fees have been calculated and reflected in the price.
          </Col>
        </Row>
      </Col>
    </Row>
  {/if}
  <Row class="trade-section">
    {#if $connected}
      <Button class="trade-button" on:click={() => phase = 'confirm'} disabled={!payRound}>Swap</Button>
    {:else}
      <WalletConnect className="trade-button">Connect to a wallet</WalletConnect>
    {/if}
  </Row>
{:else if phase === 'confirm'}
  <Row class="trade-section gx-0 align-items-center">
    <Col class="col-auto">
      <img class="icon-large" src="/icons/arrow-left.svg" alt="placeholder" on:click={() => phase = 'input'}/>
    </Col>
    <Col class="trade-phase-back">
      Go back to trade window
    </Col>
  </Row>
  <Row class="trade-margin"/>
  <Row class="trade-section trade-phase-title">
    Confirm Swap
  </Row>
  <Row class="trade-input-label trade-section">
    You pay
  </Row>
  <Row class="trade-section trade-confirm-amount gx-0">
    <Col class="col-auto trade-confirm-value">
      {payAmount}
    </Col>
    <CurrencyBadge currency={payRound ? ROUND : baseToken}/>
  </Row>
  <Row class="trade-section justify-content-center align-items-center">
    <div class="trade-divider"></div>
  </Row>
  <Row class="trade-input-label trade-section">
    You receive (minimum)
  </Row>
  <Row class="trade-section trade-confirm-amount gx-0">
    <Col class="col-auto trade-confirm-value">
      {receiveAmount * 0.99}
    </Col>
    <CurrencyBadge currency={payRound ? baseToken : ROUND}/>
  </Row>
  <Row class="trade-section gx-0">
    <Col>
      <Row class="trade-announce gx-0">
        <Col class="col-auto trade-announce-icon">
          <img class="icon-small" src="/icons/announce.svg" alt="placeholder"/>
        </Col>
        <Col>
          Transaction fees have been calculated and reflected in the price.
        </Col>
      </Row>
    </Col>
  </Row>
  {#if $connected && !isApproved && !payRound}
    <Row class="trade-section">
      <Button class="trade-button" on:click={approve} disabled={approveDisabled}>Allow Pilgrim to use your {$getPairDetail.data.pair.baseToken.symbol}</Button>
    </Row>
    <TxModal msg="Approve transaction initiated" isOpen={approveModalOpen} txHash={txHash}/>
  {/if}
  <Row class="trade-section">
    <Button class="trade-button" on:click={swap} disabled={!isApproved || confirmDisabled}>Confirm Swap</Button>
  </Row>
  <TxModal msg="Swap transaction initiated" isOpen={confirmModalOpen} txHash={txHash}/>
{:else if phase === 'submit'}
  <Row class="trade-margin"/>
  <Row class="trade-section gx-0 trade-phase-title text-end">
    <Col class="text-end">
      <img class="icon-large" src="/icons/close.svg" alt="placeholder" on:click={reset}/>
    </Col>
  </Row>
  <Row class="trade-section gx-0 trade-phase-title ">
    <Col class="col-auto trade-announce-icon">
      <img class="icon-medium" src="/icons/check-circle.svg" alt="placeholder"/>
    </Col>
    <Col>
      <Row class="align-items-center">
        <Col>
          Round trade success!
        </Col>
      </Row>
      <Row class="trade-result">
        <Col>
          You traded {payAmount} {payRound ? ROUND : baseToken} for {receiveAmount} {payRound ? baseToken : ROUND}.
        </Col>
      </Row>
      <Row class="trade-result">
        <Col>
          Transaction ID: <a href={txLink(txHash, $currentChainId)} target="_blank">{shortenAddress(txHash)}</a>
        </Col>
      </Row>
      <Row class="trade-result">
        <Col>
          <a href="/portfolio/positions" use:link>
            <Button class="button-line view-position-button">View my Positions</Button>
          </a>
        </Col>
      </Row>
    </Col>
  </Row>
{/if}
